import type { LookCategory } from "../../../shared/lookFields";

/**
 * Basic mode's controls, as data.
 *
 * A STEPPER is a 1–10 level over one or more look fields (2026-10-04: the user
 * asked for "everything 1 to 10", with pictures of the two ends). Level n puts
 * EVERY target at `lo + (n − 1)/9 · (hi − lo)` of its band — the field's
 * good-looking band from lookFields.ts, or a narrower `lo`/`hi` given here —
 * quantised to the Advanced slider's own step (see `levelToValue` in
 * applyEngine.ts). `invert` runs a target the other way (a higher level means a
 * LOWER value, e.g. the glow threshold). The FIRST target is the primary: the
 * level shown is read back from it, so it must be a continuous field (an
 * integer field collapses neighbouring levels and could not round-trip).
 *
 * `ensure` lists switches that must be on for the control to show anything.
 * While one is off the level reads 0 ("Off"); choosing any level turns them on
 * in the same Undo step.
 *
 * `less` / `more` are the captions under the two pictures, in the user's own
 * words ("Lesser / More", "Lighter / Darker"). Level 1 is the `less` side.
 */

export interface StepTarget {
  field: string;
  /** This field goes DOWN as the level goes up. */
  invert?: boolean;
  /** A narrower band for this control, inside the field's own band. */
  lo?: number;
  hi?: number;
}

/** What the two pictures of a control show (render-finetune-thumbs.mjs). */
export type PictureSubject = "gem" | "metal" | "scene" | "ground";

export interface StepperDef {
  kind: "stepper";
  id: string;
  /** The Fine-tune card it sits in. */
  category: LookCategory;
  label: string;
  /** One line under the label. */
  hint: string;
  less: string;
  more: string;
  targets: StepTarget[];
  ensure?: string[];
  /** Changes the whole picture, not only the parts the panel is scoped to. */
  global?: boolean;
  picture: PictureSubject;
}

export interface ToggleDef {
  kind: "toggle";
  id: string;
  category: LookCategory;
  label: string;
  hint: string;
  field: string;
}

/** An exact colour: a swatch plus a hex box. */
export interface ColorDef {
  kind: "color";
  id: string;
  category: LookCategory;
  label: string;
  hint: string;
  field: string;
}

/** The stone's clarity grade — five pictures (basic/clarity.ts). */
export interface ClarityDef {
  kind: "clarity";
  id: string;
  category: LookCategory;
  label: string;
  hint: string;
}

/** A row of named colours. Shown in the LEFT panels (stone tint, background). */
export interface SwatchDef {
  kind: "swatch";
  id: string;
  category: LookCategory;
  label: string;
  field: string;
  /** Static options; the metal row takes the viewer's own preset colours. */
  options: { id: string; label: string; hex: string }[] | "metalPresets";
}

export type BasicControl = StepperDef | ToggleDef | ColorDef | ClarityDef | SwatchDef;

/** How many levels every stepper has. */
export const LEVELS = 10;

const LESSER = { less: "Lesser", more: "More" } as const;
const LIGHTER = { less: "Lighter", more: "Darker" } as const;

export const BASIC_CONTROLS: BasicControl[] = [
  // ── Metal ────────────────────────────────────────────────────────────────
  // The user's own four first (RGB, Roughness, Metalness, Sparkle), then ours.
  // Measured and left OUT (render-finetune-thumbs.mjs, 2026-10-04): clear
  // coat (levels 1 vs 10 on rough gold differ by 2.2/255 — a 4% dielectric
  // layer over a ~100% reflective metal is invisible), highlight strength
  // (0.4 vs 1.6 at metalness 0.55: 0.35/255) and brushed sheen (three's
  // anisotropy without authored tangents darkens whole prongs brown instead
  // of stretching highlights, so it is not a look field either). Clear coat
  // and highlight strength stay look fields for AI Look Match; the Advanced
  // sliders reach all three.
  {
    kind: "color", id: "metalRgb", category: "metal", label: "RGB",
    hint: "The exact metal colour, as a hex code", field: "metal.color",
  },
  {
    kind: "stepper", id: "roughness", category: "metal", label: "Roughness", ...LESSER,
    hint: "Mirror polish to satin and brushed",
    targets: [{ field: "metal.roughness" }],
    picture: "metal",
  },
  {
    kind: "stepper", id: "metalness", category: "metal", label: "Metalness", ...LESSER,
    hint: "A painted, plastic look to pure metal",
    targets: [{ field: "metal.metalness" }],
    picture: "metal",
  },
  {
    kind: "stepper", id: "metalSparkle", category: "metal", label: "Sparkle", ...LESSER,
    hint: "Tiny glitter flakes across the metal surface",
    // Stronger, denser AND coarser together: at the viewer's default grain
    // (3000 per unit) a flake is a fraction of a pixel and reads as nothing.
    targets: [{ field: "metal.sparkleBump" }, { field: "metal.sparkleSize" }, { field: "metal.sparkleScale", invert: true }],
    ensure: ["metal.sparkle"],
    picture: "metal",
  },
  {
    kind: "stepper", id: "metalBrightness", category: "metal", label: "Brightness", ...LESSER,
    hint: "How brightly the metal reflects the studio",
    targets: [{ field: "metal.envMapIntensity" }],
    picture: "metal",
  },

  // ── Diamond ──────────────────────────────────────────────────────────────
  // The user's mock-up, in its order, then ours. Left out after measuring:
  // a "Twinkle" on the gem's sparkle boost — 0.94 and 1.22 rendered as two
  // different facet patterns (the lower one MORE colourful), not less and more.
  {
    kind: "stepper", id: "gemColor", category: "diamond", label: "Color", ...LIGHTER,
    hint: "How light or dark the stone reads",
    // Level up = the "Darker" picture, so the light inside the stone falls.
    targets: [{ field: "gem.envMapIntensity", invert: true }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "gemContrast", category: "diamond", label: "Contrast", ...LIGHTER,
    hint: "Soft, milky facets to deep, graphic ones",
    targets: [{ field: "gem.gammaFactor" }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "facets", category: "diamond", label: "Facets", ...LESSER,
    hint: "More inner reflections, a busier pattern",
    // Light bending is the primary: bounces are whole numbers, and two levels
    // that land on the same bounce count could not be told apart on read-back.
    targets: [{ field: "gem.ior" }, { field: "gem.bounces" }],
    picture: "gem",
  },
  {
    kind: "color", id: "gemRgb", category: "diamond", label: "RGB",
    hint: "The stone's body colour, as a hex code", field: "gem.color",
  },
  {
    kind: "stepper", id: "rainbow", category: "diamond", label: "Rainbow", ...LESSER,
    hint: "Rainbow flashes (fire) on the facet edges",
    targets: [{ field: "gem.dispersion" }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "sparkle", category: "diamond", label: "Sparkle", ...LESSER,
    hint: "A soft glow around the brightest sparkles",
    // Level 1 is a faint glow, not none: the switch below is what "none" is.
    targets: [{ field: "bloom.intensity", lo: 0.03 }, { field: "bloom.threshold", invert: true }],
    ensure: ["bloom.enabled"],
    global: true,
    picture: "gem",
  },
  {
    kind: "stepper", id: "gemReflection", category: "diamond", label: "Reflection", ...LESSER,
    hint: "Mirror glare on the stone's outer facets",
    targets: [{ field: "gem.reflectivity" }, { field: "gem.surfaceReflBoost" }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "metalOnGem", category: "diamond", label: "Metal on Gem", ...LESSER,
    hint: "The metal seen reflected inside the stones",
    targets: [{ field: "probe.gemStrength", lo: 0.1 }],
    ensure: ["probe.enabled", "probe.gemReflect"],
    global: true,
    picture: "gem",
  },
  {
    kind: "stepper", id: "brilliance", category: "diamond", label: "Brilliance", ...LESSER,
    hint: "Light coming back out through the top",
    targets: [{ field: "gem.internalEnvBoost" }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "colorDepth", category: "diamond", label: "Color depth", ...LESSER,
    hint: "Richer body colour in coloured stones",
    targets: [{ field: "gem.absorptionFactor" }],
    picture: "gem",
  },
  {
    kind: "stepper", id: "lightAngle", category: "diamond", label: "Light angle", ...LESSER,
    hint: "Turns the studio light inside the stone",
    targets: [{ field: "gem.envRotation" }],
    picture: "gem",
  },
  {
    kind: "clarity", id: "clarity", category: "diamond", label: "Clarity",
    hint: "Inner flaws, flawless to visibly included",
  },
  {
    kind: "swatch", id: "stoneColour", category: "diamond", label: "Stone colour", field: "gem.color",
    options: [
      { id: "white", label: "Colourless", hex: "#ffffff" },
      { id: "champagne", label: "Champagne", hex: "#fff0d9" },
      { id: "yellow", label: "Yellow", hex: "#ffe58a" },
      { id: "pink", label: "Pink", hex: "#ffc8dc" },
      { id: "blue", label: "Blue", hex: "#c4dcff" },
      { id: "green", label: "Green", hex: "#cdeed3" },
    ],
  },
  {
    kind: "swatch", id: "metalColour", category: "metal", label: "Metal colour", field: "metal.color",
    options: "metalPresets",
  },

  // ── Light ────────────────────────────────────────────────────────────────
  {
    kind: "stepper", id: "brightness", category: "light", label: "Brightness", ...LESSER,
    hint: "The whole picture, darker or brighter",
    targets: [{ field: "light.exposure" }, { field: "light.envIntensity" }],
    picture: "scene",
  },
  {
    kind: "stepper", id: "contrast", category: "light", label: "Contrast", ...LESSER,
    hint: "Soft and flat to punchy",
    targets: [{ field: "light.contrast" }],
    picture: "scene",
  },
  {
    kind: "stepper", id: "saturation", category: "light", label: "Color richness", ...LESSER,
    hint: "Muted to rich gold and coloured stones",
    targets: [{ field: "light.saturation" }],
    picture: "scene",
  },
  {
    kind: "swatch", id: "background", category: "light", label: "Background", field: "light.background",
    options: [
      { id: "white", label: "White", hex: "#ffffff" },
      { id: "mist", label: "Light grey", hex: "#eceef1" },
      { id: "sand", label: "Warm", hex: "#f3ede4" },
      { id: "slate", label: "Dark", hex: "#2b2d31" },
      { id: "black", label: "Black", hex: "#000000" },
    ],
  },

  // ── Ground & shadow ──────────────────────────────────────────────────────
  {
    kind: "toggle", id: "shadowOn", category: "shadow", label: "Ground shadow",
    hint: "A soft shadow under the piece", field: "shadow.enabled",
  },
  {
    kind: "stepper", id: "shadowDarkness", category: "shadow", label: "Shadow darkness", ...LESSER,
    hint: "A faint grey to a deep contact shadow",
    targets: [{ field: "shadow.darkness" }],
    ensure: ["shadow.enabled"],
    picture: "ground",
  },
  {
    kind: "stepper", id: "shadowSoftness", category: "shadow", label: "Shadow softness", ...LESSER,
    hint: "Crisp edges to a soft, diffuse shadow",
    targets: [{ field: "shadow.softness" }, { field: "shadow.blur" }, { field: "shadow.spread" }],
    ensure: ["shadow.enabled"],
    picture: "ground",
  },
  {
    kind: "stepper", id: "floorReflection", category: "shadow", label: "Floor reflection", ...LESSER,
    hint: "A glossy tabletop mirroring the piece",
    // Stronger AND sharper together: the blur runs the other way.
    targets: [{ field: "floor.reflectionStrength", lo: 0.15 }, { field: "floor.reflectionBlur", invert: true }],
    ensure: ["floor.reflection"],
    picture: "ground",
  },

  // ── Reflections ──────────────────────────────────────────────────────────
  {
    kind: "stepper", id: "reflections", category: "reflections", label: "Reflection strength", ...LESSER,
    hint: "Parts mirrored in the polished metal",
    // Metal brightness used to ride along here. As a level it would have
    // overwritten the Metal card's Brightness every time this moved — one
    // value, one control.
    // Level 1 is a faint reflection, not none (the switch is "none").
    targets: [{ field: "ssr.intensity", lo: 0.25 }, { field: "ssr.wMetal" }, { field: "ssr.wGem" }],
    ensure: ["ssr.enabled"],
    picture: "metal",
  },
];

export const BASIC_CATEGORY_META: Record<LookCategory, { icon: string; accent: string; material?: "gem" | "metal" }> = {
  diamond: { icon: "diamond", accent: "text-ed-blue", material: "gem" },
  metal: { icon: "circle", accent: "text-ed-orange", material: "metal" },
  reflections: { icon: "flare", accent: "text-ed-violet" },
  shadow: { icon: "ev_shadow", accent: "text-ed-slate" },
  light: { icon: "light_mode", accent: "text-ed-teal" },
};

/** Every look field a control reads or writes (targets, switches it ensures). */
export function controlFields(c: BasicControl): string[] {
  if (c.kind === "stepper") return [...c.targets.map((t) => t.field), ...(c.ensure ?? [])];
  if (c.kind === "clarity") return [];
  return [c.field];
}

/** The steppers, by id. */
export function stepperById(id: string): StepperDef | undefined {
  const c = BASIC_CONTROLS.find((x) => x.id === id);
  return c?.kind === "stepper" ? c : undefined;
}

/** "#b49191", "b49191" and "0xb49191" all mean the same colour; anything else is null. */
export function parseHexColor(raw: string): string | null {
  const m = /^(?:#|0x)?([0-9a-f]{6})$/i.exec(raw.trim());
  return m ? `#${m[1].toLowerCase()}` : null;
}
